import React, { useEffect, useRef, useState } from "react";
import {
  CERT_W,
  CERT_H,
  CERT_FIELDS,
  CERT_FONTS,
  CERT_CASES,
  CERT_COLOR_PRESETS,
  CERT_LAYOUT_PRESETS,
  DEFAULT_CERT_LAYOUT,
  applyCertColorPreset,
  applyCertLayoutPreset,
  resolveCertLayout,
  resolveCertSize,
  resolveCertX,
  formatCertValue,
  certFont,
  certFamily,
  certFontOptions,
  loadCertFonts,
} from "../utils/certificateLayout";

/**
 * Certificate designer — background artwork plus per-event text positions.
 *
 * Blocks are dragged directly on the preview. The canvas draws exactly what the
 * real renderer draws; transparent hit areas sit on top of it purely to catch
 * pointer events. Numeric inputs remain for the selected block, because dragging
 * is imprecise and an operator matching a background template often needs an
 * exact value.
 */

const PREVIEW_W = 380;

// Sample field sizes for rank blocks with "show total" on, so the preview reads
// "1/100" the way a real certificate will.
const PREVIEW_TOTALS = { overallRank: 100, genderRank: 48 };

/**
 * Bounds of a text block, measured from the glyphs it actually draws.
 *
 * Fixed half-widths were wrong in a way that made the editor hard to use: a
 * rank of "1" got the same 360px-wide hit area as a full name, so neighbouring
 * blocks overlapped, the selection outline floated far from its text, and the
 * wrong block caught the pointer. Measuring the real string keeps the box on
 * the glyphs, and the drag target matches what the operator sees.
 *
 * `measure` is a canvas 2D context used only for measureText.
 */
const textBox = (b, CW, measure, value, total) => {
  const { x, align } = resolveCertX(b, CW);
  let w = b.size * 2;
  let captionW = 0;
  if (measure) {
    measure.font = certFont(b, b.size);
    // Measure what is drawn — prefix, padding and "/100" included — or the
    // outline stops short of the text it belongs to.
    w = measure.measureText(formatCertValue(value, b, total)).width;
    if (b.showLabel && b.label) {
      measure.font = `${b.labelSize || 24}px ${certFamily(b.font)}`;
      captionW = measure.measureText(b.label).width;
    }
  }
  // The caption can be wider than the value it sits under ("OVERALL RANK" vs
  // "1"), and it must stay inside the box or it cannot be grabbed.
  const boxW = Math.max(w, captionW, 24);
  const pad = Math.max(8, b.size * 0.12);
  let left =
    align === "left" ? x : align === "right" ? x - boxW : x - boxW / 2;

  // A caption nudged sideways still belongs to this block: widen the box to
  // cover wherever it landed, or the operator can see it but not drag it.
  const sideShift = b.labelOffsetX ?? 0;
  let boxRight = left + boxW;
  if (b.showLabel && b.label && sideShift !== 0) {
    const capLeft =
      (align === "left" ? x : align === "right" ? x - captionW : x - captionW / 2) +
      sideShift;
    left = Math.min(left, capLeft);
    boxRight = Math.max(boxRight, capLeft + captionW);
  }

  // Vertical extent, in canvas coords relative to the baseline at b.y.
  // A caption may sit above the value (negative gap) or below it, so the box is
  // the union of both rather than an assumed downward extension.
  const ascent = b.size;
  let top = -ascent;
  let bottom = ascent * 0.25;
  if (b.showLabel && b.label) {
    const gap = b.labelGap ?? 40;
    const ls = b.labelSize || 24;
    top = Math.min(top, gap - ls);
    bottom = Math.max(bottom, gap + ls * 0.25);
  }
  return {
    x: left - pad,
    y: b.y + top - pad / 2,
    w: boxRight - left + pad * 2,
    h: bottom - top + pad,
  };
};

const blockBox = (key, L, CW = CERT_W, measure = null, previewValues = {}) => {
  const c = CW / 2;
  switch (key) {
    case "eventName":
    case "subtitle":
    case "runnerName":
    case "categoryBib":
    case "finishLabel":
    case "finishTime":
    case "overallRank":
    case "genderRank":
    case "distance":
    case "bibOnly":
    case "categoryOnly":
      return textBox(L[key], CW, measure, previewValues[key], PREVIEW_TOTALS[key]);
    case "ranks":
      return {
        x: c - L.ranks.offset - 180,
        y: L.ranks.y - L.ranks.size,
        w: L.ranks.offset * 2 + 360,
        h: L.ranks.size + 60,
      };
    case "divider":
      return { x: 100, y: L.divider.y - 14, w: CW - 200, h: 28 };
    case "splits":
      return { x: (CW - L.splits.width) / 2, y: L.splits.y - 30, w: L.splits.width, h: 260 };
    default:
      return null;
  }
};

const Num = ({ label, value, onChange, min = 0, max = 4000 }) => (
  <label className="flex flex-col gap-1 min-w-0">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
      {label}
    </span>
    <input
      type="text"
      inputMode="numeric"
      value={value ?? ""}
      onChange={(e) => {
        // Keep a leading minus when the field allows negatives, otherwise a
        // caption could only ever be nudged to the right.
        const cleaned =
          min < 0
            ? e.target.value.replace(/[^\d-]/g, "").replace(/(?!^)-/g, "")
            : e.target.value.replace(/\D/g, "");
        if (cleaned === "" || cleaned === "-") { onChange(0); return; }
        const v = parseInt(cleaned, 10);
        onChange(isNaN(v) ? 0 : Math.min(max, Math.max(min, v)));
      }}
      className="w-full h-8 px-2 text-center border border-slate-200 rounded-lg text-xs tabular-nums focus:border-slate-400 focus:outline-none"
    />
  </label>
);

/**
 * Colour swatch plus hex field.
 *
 * Both edit the same value: the native picker is how an operator finds a colour,
 * the text box is how they paste the exact brand hex a client supplied. Typing
 * only commits on a complete `#rrggbb` so the field does not fight the operator
 * mid-keystroke by rendering `#a` as black.
 */
const Color = ({ label, value, onChange }) => (
  <label className="flex flex-col gap-1 min-w-0">
    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
      {label}
    </span>
    <div className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-1.5">
      <input
        type="color"
        value={value || "#000000"}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-5 w-5 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
      />
      <input
        type="text"
        defaultValue={value || ""}
        key={value}
        onChange={(e) => {
          const v = e.target.value.trim().toUpperCase();
          if (/^#[0-9A-F]{6}$/.test(v)) onChange(v);
        }}
        className="w-full min-w-0 bg-transparent text-[11px] uppercase tabular-nums text-slate-600 focus:outline-none"
      />
    </div>
  </label>
);

/**
 * One numbered step in the settings column.
 *
 * The panel previously stacked five sibling divs of identical weight, so the
 * eye had no entry point and no sense of order — canvas size, artwork and text
 * placement all looked equally urgent. Numbering them states the sequence an
 * operator actually follows.
 */
const Step = ({ n, title, hint, children }) => (
  <section className="border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
    <div className="flex items-baseline gap-2 mb-2">
      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white">
        {n}
      </span>
      <h4 className="text-xs font-semibold text-slate-800">{title}</h4>
    </div>
    {hint && <p className="mb-2 text-[11px] leading-snug text-slate-500">{hint}</p>}
    {children}
  </section>
);

export const CertificateDesigner = ({
  eventName,
  background,
  onBackgroundChange,
  layout,
  onLayoutChange,
  // (file, family) => Promise<url>. Storage lives with the page that owns the
  // event; without it the designer simply offers no upload.
  onUploadFont,
}) => {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const [selected, setSelected] = useState("runnerName");

  // Decoded background, cached by URL.
  //
  // Dragging changes `layout` on every pointer move, which reruns the draw
  // effect. Reloading the image there meant each frame blanked the canvas to
  // white and repainted the photo from an async onload — visible as a hard
  // flicker under the cursor. The bitmap only depends on the URL, so it is
  // decoded once and reused for every subsequent repaint.
  const bgRef = useRef({ src: null, img: null });
  const [bgTick, setBgTick] = useState(0);

  useEffect(() => {
    if (!background) {
      bgRef.current = { src: null, img: null };
      setBgTick((n) => n + 1);
      return;
    }
    if (bgRef.current.src === background) return;
    let alive = true;
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      if (!alive) return;
      bgRef.current = { src: background, img: im };
      setBgTick((n) => n + 1);
    };
    im.onerror = () => {
      if (!alive) return;
      bgRef.current = { src: background, img: null };
      setBgTick((n) => n + 1);
    };
    im.src = background;
    return () => {
      alive = false;
    };
  }, [background]);

  // Two views of the same layout. Lref is in reference coordinates (1200x1600) —
  // that is what gets STORED, so a canvas resize never rewrites the numbers the
  // operator typed. Lview is Lref scaled to this event's canvas and is what the
  // preview draws and what hit areas are measured against.
  const size = resolveCertSize(layout);
  const Lref = resolveCertLayout(layout, { w: CERT_W, h: CERT_H });
  const L = resolveCertLayout(layout, size);
  const CW = size.w;
  const CH = size.h;
  const SCALE = PREVIEW_W / CW;

  // Uploaded fonts load asynchronously, and canvas draws whatever is registered
  // at that instant — so repaint once they land, or the preview stays in Arial
  // until the next drag.
  const customFonts = Lref.customFonts || [];
  const fontsKey = customFonts.map((f) => `${f.family}|${f.url}`).join(",");
  const [fontTick, setFontTick] = useState(0);
  useEffect(() => {
    let alive = true;
    loadCertFonts(customFonts).then(() => alive && setFontTick((n) => n + 1));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontsKey]);

  const [fontBusy, setFontBusy] = useState(false);
  const [fontError, setFontError] = useState("");

  const addFont = async (file) => {
    if (!file || !onUploadFont) return;
    setFontError("");
    // Family from the file name, made unique so a second upload never shadows
    // one that blocks already use.
    const base = file.name.replace(/\.[^.]+$/, "").replace(/[^\w -]+/g, " ").trim() || "Custom font";
    const taken = new Set([...customFonts.map((f) => f.family), ...CERT_FONTS.map((f) => f.value)]);
    let family = base;
    for (let i = 2; taken.has(family); i++) family = `${base} ${i}`;
    setFontBusy(true);
    try {
      // Parse it locally first: a corrupt or mislabelled file should be refused
      // here, not discovered later as Arial on every runner's certificate.
      await new FontFace(family, await file.arrayBuffer()).load();
      const url = await onUploadFont(file, family);
      // The first upload goes straight onto every text block: that is almost
      // always why it was uploaded. Later uploads are left for the operator to
      // place, so a second font never overwrites choices already made.
      const next = { ...(layout || {}), customFonts: [...customFonts, { family, url }] };
      if (customFonts.length === 0) {
        fontKeys.forEach((k) => {
          next[k] = { ...Lref[k], font: family };
        });
      }
      onLayoutChange(next);
    } catch (e) {
      setFontError(e?.message?.includes("upload") ? e.message : `"${file.name}" is not a usable font file.`);
    } finally {
      setFontBusy(false);
    }
  };

  // Uploading only adds a font to the list, and every block still says Arial —
  // so "I uploaded it and nothing changed" is the expected first experience
  // unless one click can put it on every text block at once.
  const fontKeys = CERT_FIELDS.filter((f) => f.font).map((f) => f.key);
  const usesFont = (family) => fontKeys.filter((k) => Lref[k]?.font === family).length;
  const applyFontToAll = (family) => {
    const next = { ...(layout || {}) };
    fontKeys.forEach((k) => {
      next[k] = { ...Lref[k], font: family };
    });
    onLayoutChange(next);
  };

  // Removing a font moves any block that used it back to Arial, so the stored
  // layout never names a family nothing can load.
  const removeFont = (family) => {
    const next = { ...(layout || {}), customFonts: customFonts.filter((f) => f.family !== family) };
    Object.keys(DEFAULT_CERT_LAYOUT).forEach((k) => {
      if (Lref[k] && typeof Lref[k] === "object" && Lref[k].font === family) {
        next[k] = { ...Lref[k], font: "Arial" };
      }
    });
    onLayoutChange(next);
  };

  // Sample values, in one place. The canvas draws these and the hit areas are
  // measured from these, so an outline can never sit somewhere the text isn't.
  const previewValues = {
    // Raw, exactly as the renderer receives them — formatCertValue applies each
    // block's own letter case, so the preview shows the real capitalisation.
    eventName: eventName || "Event Name",
    subtitle: L.subtitle.text,
    runnerName: "Nama Pelari",
    categoryBib: "KATEGORI  |  BIB: 1234",
    finishLabel: L.finishLabel.text,
    finishTime: "01:23:45",
    overallRank: 1,
    genderRank: 1,
    distance: "181.35",
    bibOnly: "1234",
    categoryOnly: "24H",
  };

  // Scratch context for measureText — measuring must not disturb the visible
  // canvas, whose font/fillStyle are mid-draw when hit areas are computed.
  const measureRef = useRef(null);
  if (!measureRef.current && typeof document !== "undefined") {
    measureRef.current = document.createElement("canvas").getContext("2d");
  }

  const setBlock = (key, patch) =>
    onLayoutChange({ ...(layout || {}), [key]: { ...Lref[key], ...patch } });

  const setSize = (patch) => onLayoutChange({ ...(layout || {}), ...patch });

  // ── Dragging: pointer delta in preview px → reference px.
  // Deltas are divided by the preview scale to reach canvas px, then by the
  // canvas/reference ratio so what we store stays in reference coordinates.
  const onPointerDown = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(key);
    const startY = e.clientY;
    const startX = e.clientX;
    dragRef.current = {
      key,
      startY,
      startX,
      originY: Lref[key].y,
      originOffset: Lref[key].offset,
      // Null x means "centred". Dragging horizontally has to commit to a real
      // coordinate, so resolve the implied centre now and move from there.
      originX: Lref[key].x == null ? CERT_W / 2 : Lref[key].x,
      hasX: "x" in Lref[key],
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dy = Math.round((e.clientY - d.startY) / SCALE / (CH / CERT_H));
    const patch = { y: Math.max(0, Math.min(CERT_H, d.originY + dy)) };
    const dx = Math.round((e.clientX - d.startX) / SCALE / (CW / CERT_W));
    // Ranks are a legacy symmetric pair: horizontal dragging widens the gap
    // rather than moving the block off-centre, which would break the symmetry.
    if (d.key === "ranks" && d.originOffset != null) {
      patch.offset = Math.max(0, Math.min(CERT_W / 2, d.originOffset + dx));
    } else if (d.hasX) {
      // Every other text block moves freely in both axes, because client
      // artwork does not put its values on the centre line.
      patch.x = Math.max(0, Math.min(CERT_W, d.originX + dx));
    }
    setBlock(d.key, patch);
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  // ── Preview render — same drawing calls as the real certificate.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // Only on a real size change: assigning width/height wipes the canvas even
    // when the value is identical, which is a second source of drag flicker.
    if (canvas.width !== CW) canvas.width = CW;
    if (canvas.height !== CH) canvas.height = CH;

    const draw = (bgImg) => {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, CW, CH);
      if (bgImg) {
        // Cover, matching the real renderer: stretching warps artwork whose
        // aspect ratio differs from the canvas.
        const sc = Math.max(CW / bgImg.width, CH / bgImg.height);
        const dw = bgImg.width * sc, dh = bgImg.height * sc;
        ctx.drawImage(bgImg, (CW - dw) / 2, (CH - dh) / 2, dw, dh);
      }

      if (L.showHeaderBand && L.headerHeight > 0 && !bgImg) {
        ctx.fillStyle = "#F3F4F6";
        ctx.fillRect(0, 0, CW, L.headerHeight);
        ctx.strokeStyle = "#E5E7EB";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, L.headerHeight);
        ctx.lineTo(CW, L.headerHeight);
        ctx.stroke();
      }

      // Same block-drawing contract as the real renderer in RunnerDetailsModal —
      // alignment, font, prefix/suffix and caption all resolved identically, so
      // the preview cannot drift from the downloaded PNG.
      const drawBlock = (key, raw) => {
        const b = L[key];
        if (!b || b.show === false) return;
        const { x, align } = resolveCertX(b, CW);
        ctx.textAlign = align;
        ctx.fillStyle = b.color;
        ctx.font = certFont(b, b.size);
        ctx.fillText(formatCertValue(raw, b, PREVIEW_TOTALS[key]), x, b.y);
        if (b.showLabel && b.label) {
          ctx.fillStyle = b.labelColor;
          ctx.font = `${b.labelSize}px ${certFamily(b.font)}`;
          ctx.fillText(b.label, x + (b.labelOffsetX ?? 0), b.y + (b.labelGap ?? 40));
        }
      };

      Object.entries(previewValues).forEach(([key, value]) => {
        // Subtitle and the finish label draw nothing when their wording is
        // cleared, which is how an operator removes them without hiding the
        // block outright.
        if ((key === "subtitle" || key === "finishLabel") && !L[key].text) return;
        if (key === "overallRank" || key === "genderRank") return;
        drawBlock(key, value);
      });

      if (L.divider.show) {
        ctx.strokeStyle = L.divider.color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(100, L.divider.y);
        ctx.lineTo(CW - 100, L.divider.y);
        ctx.stroke();
      }

      if (L.ranks.show) {
        ctx.textAlign = "center";
        ["OVERALL RANK", "GENDER RANK"].forEach((lbl, i) => {
          const x = CW / 2 + (i === 0 ? -L.ranks.offset : L.ranks.offset);
          ctx.fillStyle = L.ranks.color;
          ctx.font = `bold ${L.ranks.size}px Arial`;
          ctx.fillText("1", x, L.ranks.y);
          ctx.fillStyle = L.ranks.labelColor;
          ctx.font = "24px Arial";
          ctx.fillText(lbl, x, L.ranks.y + 40);
        });
      } else {
        drawBlock("overallRank", 1);
        drawBlock("genderRank", 1);
      }

      if (L.splits.show) {
        const tw = L.splits.width;
        const tx = (CW - tw) / 2;
        let curY = L.splits.y;
        ctx.textAlign = "left";
        ctx.fillStyle = L.splits.labelColor;
        ctx.font = "bold 24px Arial";
        ctx.fillText("SPLITS", tx, curY);
        curY += 20;
        ctx.save();
        ctx.globalAlpha = 0.35;
        ctx.strokeStyle = L.splits.labelColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(tx, curY);
        ctx.lineTo(tx + tw, curY);
        ctx.stroke();
        ctx.restore();
        curY += 60;
        ["CP 1", "CP 2", "Finish"].forEach((n, i) => {
          ctx.textAlign = "left";
          ctx.font = `${L.splits.size}px ${certFamily(L.splits.font)}`;
          ctx.fillStyle = L.splits.labelColor;
          ctx.fillText(n, tx + 10, curY + i * 50);
          ctx.textAlign = "right";
          ctx.font = `bold ${L.splits.size}px ${certFamily(L.splits.font)}`;
          ctx.fillStyle = L.splits.color;
          ctx.fillText("00:2" + i + ":00", tx + tw - 10, curY + i * 50);
        });
      }

      if (L.footer.show) {
        ctx.fillStyle = L.footer.bgColor;
        ctx.fillRect(0, CH - 80, CW, 80);
        ctx.textAlign = "center";
        ctx.fillStyle = L.footer.color;
        ctx.font = "bold 24px Arial";
        ctx.fillText(L.footer.text, CW / 2, CH - 30);
      }
    };

    // Synchronous: the bitmap is already decoded (or absent). Repaints during a
    // drag now land in one frame instead of blanking and re-decoding.
    draw(background ? bgRef.current.img : null);
  }, [eventName, background, layout, bgTick, fontTick]);

  const sel = CERT_FIELDS.find((f) => f.key === selected);

  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5 items-start">
      {/* ── Preview + drag layer */}
      <div className="lg:sticky lg:top-4">
        <div
          ref={stageRef}
          className="relative select-none touch-none"
          style={{ width: PREVIEW_W, height: CH * SCALE }}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
        >
          <canvas
            ref={canvasRef}
            style={{ width: PREVIEW_W, height: CH * SCALE }}
            className="border border-slate-200 rounded-card bg-white"
          />
          {CERT_FIELDS.map((f) => {
            if (f.toggle && !L[f.key].show) return null;
            const b = blockBox(f.key, L, CW, measureRef.current, previewValues);
            if (!b) return null;
            const active = selected === f.key;
            return (
              <div
                key={f.key}
                onPointerDown={onPointerDown(f.key)}
                title={`Drag ${f.label}`}
                className={`absolute cursor-move rounded transition-colors ${
                  active
                    ? "border border-teal-500 bg-teal-500/10"
                    : "border border-transparent hover:border-slate-300 hover:bg-slate-900/5"
                }`}
                style={{
                  left: b.x * SCALE,
                  top: b.y * SCALE,
                  width: b.w * SCALE,
                  height: b.h * SCALE,
                }}
              />
            );
          })}
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          Drag a block to move it · {CW}×{CH}
        </p>
      </div>

      {/* ── Settings column: three ordered steps, not five equal siblings. */}
      <div className="space-y-4">
        {/* Step 1 — artwork. Deliberately first: uploading a file sets the canvas
            size for you, which is the answer to step 2 most of the time. */}
        <Step
          n="1"
          title="Background artwork"
          hint="Optional. The canvas adopts this file's dimensions, so the client's design is never redrawn to fit."
        >
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              onBackgroundChange(file);
              // Adopt the artwork's own dimensions. The operator should not have
              // to open the file to read its size, and a mismatch here is exactly
              // what crops or distorts the client's design.
              if (!file) return;
              const url = URL.createObjectURL(file);
              const im = new Image();
              im.onload = () => {
                if (im.width >= 200 && im.height >= 200) {
                  setSize({ canvasWidth: im.width, canvasHeight: im.height });
                }
                URL.revokeObjectURL(url);
              };
              im.onerror = () => URL.revokeObjectURL(url);
              im.src = url;
            }}
            className="block w-full text-xs text-slate-600 file:mr-2 file:rounded-lg file:border file:border-slate-200 file:bg-slate-50 file:px-3 file:py-1.5 file:text-xs file:font-medium"
          />

          {/* The client's own typeface. Uploaded straight away rather than on
              save, so the draft kept in this browser holds a real URL. */}
          {onUploadFont && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium text-slate-600">Custom fonts</span>
                <label
                  className={`cursor-pointer rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 ${
                    fontBusy ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  {fontBusy ? "Uploading…" : "Upload .ttf / .otf / .woff"}
                  <input
                    type="file"
                    accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
                    className="hidden"
                    onChange={(e) => {
                      addFont(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              <p className="mt-1 text-[10px] leading-snug text-slate-500">
                Use the file the client&apos;s designer supplied; they hold its licence. Pick it per block under Font.
              </p>
              {fontError && <p className="mt-1 text-[11px] text-red-600">{fontError}</p>}
              {customFonts.length > 0 && (
                <ul className="mt-1.5 space-y-1">
                  {customFonts.map((f) => (
                    <li
                      key={f.family}
                      className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1"
                    >
                      <span className="truncate text-[13px] text-slate-800" style={{ fontFamily: certFamily(f.family) }}>
                        {f.family} · 1234 01:23:45
                      </span>
                      <span className="flex shrink-0 items-center gap-3">
                        <span className="text-[10px] text-slate-400">
                          {usesFont(f.family)}/{fontKeys.length} blocks
                        </span>
                        <button
                          type="button"
                          onClick={() => applyFontToAll(f.family)}
                          className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-100"
                        >
                          Use on all text
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFont(f.family)}
                          className="text-[11px] text-slate-400 hover:text-red-600"
                        >
                          Remove
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Step>

        {/* Step 2 — canvas size. Presets lead because they are one click and
            cover most deliveries; the exact fields sit underneath for artwork
            that arrives in an odd size. */}
        <Step
          n="2"
          title="Certificate size"
          hint="Scaled to cover and centre-cropped, so a mismatched ratio loses edges rather than distorting the design."
        >
          <div className="grid grid-cols-2 gap-1.5">
            {[
              ["Portrait 3:4", 1200, 1600],
              ["A4 portrait", 1240, 1754],
              ["A4 landscape", 1754, 1240],
              ["Square", 1400, 1400],
              ["Landscape 4:3", 1600, 1200],
            ].map(([lbl, w, h]) => (
              <button
                key={lbl}
                type="button"
                onClick={() => setSize({ canvasWidth: w, canvasHeight: h })}
                className={`rounded-lg border px-2 py-1.5 text-[11px] font-medium transition-colors ${
                  CW === w && CH === h
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Num label="Width" value={CW} max={6000} onChange={(v) => setSize({ canvasWidth: v })} />
            <Num label="Height" value={CH} max={6000} onChange={(v) => setSize({ canvasHeight: v })} />
          </div>
        </Step>

        {/* Step 3 — starting point. A finished client design needs almost every
            built-in block switched OFF, and doing that one chip at a time is
            nine clicks before the operator can even start placing values. */}
        <Step
          n="3"
          title="Starting point"
          hint="For finished client artwork, switch off the blocks the design already draws."
        >
          <div className="flex flex-col gap-1.5">
            {CERT_LAYOUT_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => onLayoutChange(applyCertLayoutPreset(layout, p))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-left transition-colors hover:bg-slate-50"
              >
                <span className="block text-[11px] font-semibold text-slate-700">{p.label}</span>
                <span className="mt-0.5 block text-[10px] leading-snug text-slate-500">{p.hint}</span>
              </button>
            ))}
          </div>
        </Step>

        {/* Step 4 — palette. Ahead of placement because contrast is the thing
            that makes a preview readable enough to place blocks against. */}
        <Step
          n="4"
          title="Text colour"
          hint="Set every block at once, then override individual blocks below. Positions are left alone."
        >
          <div className="flex flex-wrap gap-1.5">
            {CERT_COLOR_PRESETS.map((p) => {
              // Reflect the layout rather than remembering the last click: the
              // operator can also recolour blocks by hand, and a preset chip
              // that stayed lit after that would be lying.
              const applied = Object.entries(p.colors).every(([k, patch]) =>
                Object.entries(patch).every(([field, v]) => Lref[k]?.[field] === v),
              );
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => onLayoutChange(applyCertColorPreset(layout, p))}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    applied
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span
                    className="h-3 w-3 rounded-full border border-slate-300"
                    style={{ background: p.swatch }}
                  />
                  {p.label}
                </button>
              );
            })}
          </div>
        </Step>

        {/* Step 5 — text placement. The chip row selects; the card below edits
            only what is selected, so the column never grows a wall of inputs. */}
        <Step
          n="5"
          title="Text blocks"
          hint="Pick a block, then drag it on the preview or fine-tune the numbers."
        >
          <div className="flex flex-wrap gap-1.5">
            {CERT_FIELDS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setSelected(f.key)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  selected === f.key
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

        {sel && (
          <div className="mt-2 rounded-card border border-slate-200 bg-slate-50/60 px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-700">{sel.label}</span>
              {sel.toggle && (
                <button
                  type="button"
                  onClick={() => setBlock(sel.key, { show: !L[sel.key].show })}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${
                    L[sel.key].show
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 text-slate-500"
                  }`}
                >
                  {L[sel.key].show ? "shown" : "hidden"}
                </button>
              )}
            </div>
            {/* Numbers, then colours, in separate labelled rows. Mixed into one
                grid the swatches wrapped under headings like "TEXT" and read as
                empty text fields, which hid the font-size box next to them. */}
            <div className="grid grid-cols-2 gap-2">
              {sel.align && (
                <Num
                  label="X"
                  value={Lref[sel.key].x == null ? CERT_W / 2 : Lref[sel.key].x}
                  onChange={(v) => setBlock(sel.key, { x: v })}
                  max={CERT_W}
                />
              )}
              {sel.pos && (
                <Num label="Y" value={Lref[sel.key].y} onChange={(v) => setBlock(sel.key, { y: v })} max={CERT_H} />
              )}
              {sel.size && (
                <Num label="Font size" value={Lref[sel.key].size} onChange={(v) => setBlock(sel.key, { size: v })} max={300} />
              )}
              {sel.offset && (
                <Num label="Gap" value={Lref[sel.key].offset} onChange={(v) => setBlock(sel.key, { offset: v })} max={CERT_W / 2} />
              )}
              {sel.width && (
                <Num label="Width" value={Lref[sel.key].width} onChange={(v) => setBlock(sel.key, { width: v })} max={CERT_W} />
              )}
            </div>

            {sel.align && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 min-w-0">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    Align
                  </span>
                  <div className="flex h-8 overflow-hidden rounded-lg border border-slate-200">
                    {["left", "center", "right"].map((a) => (
                      <button
                        key={a}
                        type="button"
                        onClick={() => setBlock(sel.key, { align: a })}
                        title={`Align ${a}`}
                        className={`flex-1 text-[10px] font-medium capitalize transition-colors ${
                          (Lref[sel.key].align || "center") === a
                            ? "bg-slate-900 text-white"
                            : "bg-white text-slate-500 hover:bg-slate-50"
                        }`}
                      >
                        {a[0].toUpperCase()}
                      </button>
                    ))}
                  </div>
                </label>
                {sel.font && (
                  <label className="flex flex-col gap-1 min-w-0">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                      Font
                    </span>
                    <select
                      value={Lref[sel.key].font || "Arial"}
                      onChange={(e) => setBlock(sel.key, { font: e.target.value })}
                      className="h-8 w-full rounded-lg border border-slate-200 bg-white px-1.5 text-[11px] text-slate-600 focus:border-slate-400 focus:outline-none"
                    >
                      {certFontOptions(customFonts).map((f) => (
                        <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}

            {sel.textCase && (
              <label className="mt-2 flex flex-col gap-1">
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  Letter case
                </span>
                <select
                  value={Lref[sel.key].textCase || "none"}
                  onChange={(e) => setBlock(sel.key, { textCase: e.target.value })}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-1.5 text-[11px] text-slate-600 focus:border-slate-400 focus:outline-none"
                >
                  {CERT_CASES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {sel.font && (
              <button
                type="button"
                onClick={() => setBlock(sel.key, { bold: Lref[sel.key].bold === false })}
                className={`mt-2 rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  Lref[sel.key].bold === false
                    ? "border-slate-200 text-slate-500 hover:bg-slate-50"
                    : "border-slate-900 bg-slate-900 text-white"
                }`}
              >
                Bold
              </button>
            )}

            {/* Affixes shape the value the way finished templates print it —
                `[001]` is prefix "[", pad 3, suffix "]". Padding cannot be
                faked by typing, because the value comes from the results. */}
            {sel.affix && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1 min-w-0">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    Prefix
                  </span>
                  <input
                    type="text"
                    value={Lref[sel.key].prefix ?? ""}
                    onChange={(e) => setBlock(sel.key, { prefix: e.target.value })}
                    className="h-8 w-full rounded-lg border border-slate-200 px-2 text-center text-xs focus:border-slate-400 focus:outline-none"
                  />
                </label>
                {sel.pad && (
                  <Num
                    label="Digits"
                    value={Lref[sel.key].pad ?? 0}
                    onChange={(v) => setBlock(sel.key, { pad: v })}
                    max={6}
                  />
                )}
                <label className="flex flex-col gap-1 min-w-0">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    Suffix
                  </span>
                  <input
                    type="text"
                    value={Lref[sel.key].suffix ?? ""}
                    onChange={(e) => setBlock(sel.key, { suffix: e.target.value })}
                    className="h-8 w-full rounded-lg border border-slate-200 px-2 text-center text-xs focus:border-slate-400 focus:outline-none"
                  />
                </label>
              </div>
            )}

            {/* "1/100" — the placing over the field size. The total counts
                placings awarded in the runner's category (the gender's, for
                gender rank), so non-finishers never inflate it. */}
            {sel.total && (
              <div className="mt-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-slate-600">
                    Show total, e.g. 1/{PREVIEW_TOTALS[sel.key] ?? 100}
                  </span>
                  <button
                    type="button"
                    onClick={() => setBlock(sel.key, { showTotal: !Lref[sel.key].showTotal })}
                    className={`rounded-full border px-2 py-0.5 text-[11px] ${
                      Lref[sel.key].showTotal
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 text-slate-500"
                    }`}
                  >
                    {Lref[sel.key].showTotal ? "shown" : "hidden"}
                  </button>
                </div>
                {Lref[sel.key].showTotal && (
                  <label className="mt-1.5 flex items-center gap-2">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                      Separator
                    </span>
                    <input
                      type="text"
                      value={Lref[sel.key].totalSep ?? "/"}
                      onChange={(e) => setBlock(sel.key, { totalSep: e.target.value })}
                      placeholder="/"
                      className="h-8 w-20 rounded-lg border border-slate-200 px-2 text-center text-xs focus:border-slate-400 focus:outline-none"
                    />
                  </label>
                )}
              </div>
            )}

            {/* Caption under a rank. Off when the artwork already prints its own
                heading, which is the usual case for a finished design. */}
            {sel.caption && (
              <div className="mt-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-slate-600">Caption</span>
                  <button
                    type="button"
                    onClick={() => setBlock(sel.key, { showLabel: !Lref[sel.key].showLabel })}
                    className={`rounded-full border px-2 py-0.5 text-[11px] ${
                      Lref[sel.key].showLabel
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 text-slate-500"
                    }`}
                  >
                    {Lref[sel.key].showLabel ? "shown" : "hidden"}
                  </button>
                </div>
                {Lref[sel.key].showLabel && (
                  <>
                    <input
                      type="text"
                      value={Lref[sel.key].label ?? ""}
                      onChange={(e) => setBlock(sel.key, { label: e.target.value })}
                      placeholder="Caption text"
                      className="mt-1.5 h-8 w-full rounded-lg border border-slate-200 px-2 text-xs focus:border-slate-400 focus:outline-none"
                    />
                    <div className="mt-1.5 grid grid-cols-2 gap-2">
                      <Num
                        label="Caption size"
                        value={Lref[sel.key].labelSize ?? 24}
                        onChange={(v) => setBlock(sel.key, { labelSize: v })}
                        max={200}
                      />
                      <Num
                        label="Gap"
                        value={Lref[sel.key].labelGap ?? 40}
                        onChange={(v) => setBlock(sel.key, { labelGap: v })}
                        max={400}
                      />
                      {/* Horizontal nudge, so a caption can sit beside its
                          value — next to an icon, say — instead of directly
                          beneath it. Negative moves it left. */}
                      <Num
                        label="Side"
                        value={Lref[sel.key].labelOffsetX ?? 0}
                        onChange={(v) => setBlock(sel.key, { labelOffsetX: v })}
                        min={-2000}
                        max={2000}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {(sel.color || sel.labelColor || sel.bgColor) && (
              <div className="mt-2 border-t border-slate-200/70 pt-2">
                <span className="mb-1.5 block text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  Colour
                </span>
                <div className="grid grid-cols-2 gap-2">
                  {sel.color && (
                    <Color
                      label={sel.color}
                      value={Lref[sel.key].color}
                      onChange={(v) => setBlock(sel.key, { color: v })}
                    />
                  )}
                  {sel.labelColor && (
                    <Color
                      label={sel.labelColor}
                      value={Lref[sel.key].labelColor}
                      onChange={(v) => setBlock(sel.key, { labelColor: v })}
                    />
                  )}
                  {sel.bgColor && (
                    <Color
                      label={sel.bgColor}
                      value={Lref[sel.key].bgColor}
                      onChange={(v) => setBlock(sel.key, { bgColor: v })}
                    />
                  )}
                </div>
              </div>
            )}

            {sel.text && (
              <label className="mt-2 flex flex-col gap-1">
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  Wording
                </span>
                <input
                  type="text"
                  value={Lref[sel.key].text ?? ""}
                  onChange={(e) => setBlock(sel.key, { text: e.target.value })}
                  placeholder={sel.label}
                  className="w-full h-8 px-2 border border-slate-200 rounded-lg text-xs focus:border-slate-400 focus:outline-none"
                />
              </label>
            )}
          </div>
        )}
        </Step>

        {/* Reset. Two buttons, not one: the artwork and the text placement are
            stored separately, and the old single "reset layout" control moved
            the text back to defaults while leaving the client's background in
            place — which reads as "reset did nothing" against a full-bleed
            design. Naming each target makes the split obvious. */}
        <div className="grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => onLayoutChange({ ...DEFAULT_CERT_LAYOUT })}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
          >
            Reset text layout
          </button>
          <button
            type="button"
            disabled={!background}
            onClick={() => onBackgroundChange(null)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Remove artwork
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificateDesigner;
